import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { environment } from '../../../environments/environment';

interface ScheduledTaskQueueItem {
  visitId: number;
  dueUtc: string;
}

interface ScheduledTaskInfo {
  taskId: string;
  tenantPrefix: string | null;
  kind: string;
  taskType: string;
  clientId: string | null;
  posId: number | null;
  posName: string | null;
  nextRunUtc: string;
  timeZoneIanaId: string | null;
  status: string;
  executedUtc: string | null;
  lastRunSucceeded: boolean | null;
  lastRunError: string | null;
  accountId: number | null;
  accountEmail: string | null;
  reportPeriod: string | null;
  reportStartHour: number | null;
  periodStartUtc: string | null;
  periodEndUtc: string | null;
  autoCheckoutMode: number | null;
  autoCheckoutHour: number | null;
  queuedVisitCount: number | null;
  visitQueue: ScheduledTaskQueueItem[] | null;
  processedVisitCount: number | null;
  processedVisits: ScheduledTaskQueueItem[] | null;
}

@Component({
  selector: 'app-scheduled-tasks',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, DatePipe],
  templateUrl: './scheduled-tasks.component.html'
})
export class ScheduledTasksComponent implements OnInit, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly variables = inject(MatwinsVariablesService);

  readonly tasks = signal<ScheduledTaskInfo[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly expandedTaskId = signal<string | null>(null);
  readonly lastRefresh = signal<Date | null>(null);

  // '' = tous les clients ; '' = tous les sites ; '' = tous les statuts
  readonly selectedTenant = signal<string>('');
  readonly selectedPosKey = signal<string>('');
  readonly selectedStatus = signal<string>('');

  readonly executedCount = computed(() =>
    this.tasks().filter(t => t.status === 'Executed').length);

  readonly tenantOptions = computed(() => {
    const prefixes = new Set<string>();
    for (const t of this.tasks())
      if (t.tenantPrefix)
        prefixes.add(t.tenantPrefix);
    return [...prefixes].sort();
  });

  // Clé composite "tenant|posId" : les identifiants POS ne sont uniques que par tenant.
  readonly posOptions = computed(() => {
    const tenant = this.selectedTenant();
    const map = new Map<string, { key: string; label: string }>();
    for (const t of this.tasks()) {
      if (t.posId == null)
        continue;
      if (tenant && t.tenantPrefix !== tenant)
        continue;
      const key = `${t.tenantPrefix}|${t.posId}`;
      if (!map.has(key)) {
        const name = t.posName ?? `POS ${t.posId}`;
        map.set(key, { key, label: tenant ? name : `${name} (${t.tenantPrefix})` });
      }
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  });

  readonly filteredTasks = computed(() => {
    const tenant = this.selectedTenant();
    const posKey = this.selectedPosKey();
    const status = this.selectedStatus();
    let list = this.tasks();
    if (tenant)
      list = list.filter(t => t.tenantPrefix === tenant);
    if (posKey) {
      const sep = posKey.lastIndexOf('|');
      const posTenant = posKey.slice(0, sep);
      const posId = posKey.slice(sep + 1);
      list = list.filter(t => t.tenantPrefix === posTenant && t.posId?.toString() === posId);
    }
    if (status === 'Executed:success')
      list = list.filter(t => t.status === 'Executed' && t.lastRunSucceeded === true);
    else if (status === 'Executed:failed')
      list = list.filter(t => t.status === 'Executed' && t.lastRunSucceeded === false);
    else if (status)
      list = list.filter(t => t.status === status);
    return list;
  });

  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    void this.refresh();
    this.refreshTimer = setInterval(() => void this.refresh(), 30000);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer)
      clearInterval(this.refreshTimer);
  }

  private buildHeaders(): HttpHeaders {
    const accessToken = this.variables.getAccessToken();
    let headers = new HttpHeaders({
      'X-Custom-Matwins-BrowserId': this.variables.browserId ?? '',
      'X-Custom-Matwins-ClientId': this.variables.clientId ?? '',
      'X-Custom-Matwins-PosId': this.variables.posId?.toString() ?? ''
    });
    if (accessToken)
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    return headers;
  }

  async refresh(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const result = await firstValueFrom(
        this.http.get<ScheduledTaskInfo[]>(`${environment.apiBaseUrl}/api/scheduledtasks`, { headers: this.buildHeaders() })
      );
      this.tasks.set(result ?? []);
      this.lastRefresh.set(new Date());
    } catch (e: any) {
      this.error.set(e?.error?.detail ?? e?.message ?? 'Loading error');
    } finally {
      this.loading.set(false);
    }
  }

  async clearExecuted(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      await firstValueFrom(
        this.http.delete(`${environment.apiBaseUrl}/api/scheduledtasks/executed`, { headers: this.buildHeaders() })
      );
      await this.refresh();
    } catch (e: any) {
      this.error.set(e?.error?.detail ?? e?.message ?? 'Delete error');
      this.loading.set(false);
    }
  }

  // Une instance exécutée et sa replanification partagent le même taskId :
  // la clé de ligne distingue chaque exécution par son statut et son horodatage.
  rowKey(task: ScheduledTaskInfo): string {
    return `${task.taskId}|${task.status}|${task.executedUtc ?? task.nextRunUtc}`;
  }

  toggleTask(task: ScheduledTaskInfo): void {
    const key = this.rowKey(task);
    this.expandedTaskId.set(this.expandedTaskId() === key ? null : key);
  }

  onTenantChange(value: string): void {
    this.selectedTenant.set(value);
    // Réinitialise le site s'il n'appartient pas au client choisi
    if (this.selectedPosKey() && !this.posOptions().some(o => o.key === this.selectedPosKey()))
      this.selectedPosKey.set('');
  }

  statusLabel(task: ScheduledTaskInfo): string {
    if (task.status === 'Executed')
      return task.lastRunSucceeded === false ? 'Failed' : 'Executed';
    return task.status;
  }

  statusBadgeClass(task: ScheduledTaskInfo): string {
    if (task.status === 'Executed')
      return task.lastRunSucceeded === false
        ? 'bg-red-100 text-red-700'
        : 'bg-green-100 text-green-700';
    if (task.status === 'Running')
      return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  }

  kindBadgeClass(kind: string): string {
    switch (kind) {
      case 'Report': return 'bg-blue-100 text-blue-700';
      case 'AutoCheckout': return 'bg-green-100 text-green-700';
      case 'BuildTree': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  kindLabel(task: ScheduledTaskInfo): string {
    if (task.kind === 'AutoCheckout')
      return `Checkout ${this.checkoutModeLabel(task.autoCheckoutMode)}`;
    if (task.kind === 'Report')
      return `Report ${task.reportPeriod ?? ''}`;
    return task.taskType;
  }

  checkoutModeLabel(mode: number | null): string {
    if (mode === 1) return 'Fixed hour';
    if (mode === 2) return 'Sliding';
    return mode?.toString() ?? '';
  }
}
